import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CodeIcon from "@mui/icons-material/Code";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import GrassIcon from "@mui/icons-material/Grass";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import PersonIcon from "@mui/icons-material/Person";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SyncAltIcon from "@mui/icons-material/SyncAlt";

/**
 * Every project shown on the site. The home page renders the `featured` subset,
 * the projects page renders all of them.
 *
 * The home page currently features the three freelance platforms only: they
 * are the work that shipped to paying clients and is running in production,
 * and three cards fill one row cleanly where six left a ragged second row.
 * The academic and personal builds are all still on the projects page. When
 * the AI projects land they should be featured here too, which is why this is
 * still a per-project flag rather than a filter on category.
 *
 * @typedef {object} Project
 * @property {string}   slug      stable key (also used for React keys)
 * @property {string}   title
 * @property {Function} Icon      MUI icon component
 * @property {string}   summary   long-form description shown in the card body
 * @property {string[]} tech      technologies / keywords rendered as tags
 * @property {string}   date
 * @property {string}   category  Freelance | Academic | Personal
 * @property {boolean}  featured  shown on the home page
 * @property {string}   [github]  omitted when the repo is private
 * @property {string}   [live]    omitted when there is no public deployment.
 *                                Its presence is what marks a project as
 *                                shipped; a project still in testing has no
 *                                live link and must say so in its summary.
 */

/** @type {Project[]} */
export const projects = [
  // ---------------------------------------------------------------------------
  // NEXT: the AI projects go here, at the top of the list and `featured: true`.
  //
  // The hero claims LLM applications, RAG systems and AI agents. That claim is
  // currently supported (the AUB Diploma, the Meta certificate, the computer
  // vision in Smart Presence, the AI integration on AlgoPath and Travel Code)
  // but not demonstrated, and this is the slot that demonstrates it. The
  // ML-driven load testing tool belongs here too; it is the piece that joins
  // both halves of the claim in one project.
  //
  // Entries are ordered strongest first and the home page shows whichever are
  // featured, so adding one here is the only edit needed. Shape:
  //
  //   {
  //     slug: "stable-key",
  //     title: "Name",
  //     Icon: SomeMuiIcon,            // imported above, never inline JSX
  //     summary: "What it does, what you built, what it runs on.",
  //     tech: ["RAG", "OpenAI", "Spring Boot"],
  //     image: "/name.webp",          // see the Images section of CLAUDE.md
  //     date: "Mon 2026",
  //     category: "Personal",         // Freelance | Academic | Personal
  //     featured: true,
  //     github: "...",                // omit when the repo is private
  //     live: "...",                  // omit when there is no deployment
  //   }
  // ---------------------------------------------------------------------------
  {
    slug: "serve",
    title: "Serve",
    Icon: SportsTennisIcon,
    summary:
      "Serve is a live freelance padel booking platform built for a business in Lebanon. Clients reserve a court directly online, confirm payment, and manage their playing schedule across the two available courts, Zayd and Taym. The experience works on both mobile and web, with a user flow designed around fast reservations.",
    tech: [
      "Freelance",
      "Spring Boot",
      "TypeScript",
      "Booking System",
      "Mobile app showcase",
      "Payment Flow",
      "Live Product",
      "UX",
    ],
    image: "/serve.webp",
    date: "Feb 2026",
    category: "Freelance",
    featured: true,
    live: "https://servelb.com/",
  },
  {
    slug: "travel-code",
    title: "Travel Code",
    Icon: FlightTakeoffIcon,
    summary:
      "Travel Code is a freelance website for a travel company in Lebanon, currently in testing. The platform presents travel packages and customer options clearly while integrating an AI-supported assistance flow that helps users explore, filter and select suitable travel choices based on their needs.",
    tech: [
      "Freelance",
      "Spring Boot",
      "TypeScript",
      "Travel Platform",
      "AI Integration",
      "Client Support",
      "Responsive",
      "Product Design",
    ],
    image: "/travel-code.webp",
    date: "2026",
    category: "Freelance",
    featured: true,
  },
  {
    slug: "kinetic-haven",
    title: "Kinetic Haven",
    Icon: HealthAndSafetyIcon,
    summary:
      "Kinetic Haven is a physiotherapy platform created for a clinic in Lebanon, currently in testing. It combines a modern, customized visual identity with practical clinic needs such as appointment booking, service discovery, and a streamlined payment flow for patient sessions.",
    tech: [
      "Freelance",
      "Healthcare Platform",
      "Booking System",
      "Payments Integration",
      "Custom UI",
      "Responsive Design",
      "Lebanon",
    ],
    image: "/kinetic-haven.webp",
    date: "2026",
    category: "Freelance",
    featured: true,
  },
  {
    slug: "automated-attendance-system",
    title: "Smart Presence: Automated Attendance System",
    Icon: CameraAltIcon,
    summary:
      "My senior capstone, built with a team of 4 and placed 3rd of 12. A classroom camera records attendance on its own, using YOLOv8 to detect students and LBPH to recognise them. I owned the backend, integrated the models into the service, secured access with OAuth, and deployed it end to end.",
    tech: [
      "YOLOv8",
      "LBPH",
      "Computer Vision",
      "OAuth",
      "Laravel",
      "PostgreSQL",
      "React Vite",
      "AWS",
      "Docker",
    ],
    image: "/attendance.webp",
    date: "Apr 2025",
    category: "Academic",
    featured: false,
    github: "https://github.com/Chafic123/Automated-Attendance-System-Backend",
  },
  {
    slug: "terragrow",
    title: "TerraGrow",
    Icon: GrassIcon,
    summary:
      "A farm simulation that teaches sustainable agriculture from live NASA data. Players irrigate, fertilise and make crop-care decisions on a virtual farm, then watch soil health, vegetation and yield respond over time, driven by real SMAP, MODIS and AppEEARS readings. Built in two days and placed 12th of 60 teams at the NASA Space Apps Challenge.",
    tech: [
      "NASA SMAP",
      "MODIS",
      "AppEEARS",
      "Data Visualization",
      "Simulation",
      "React",
      "Rapid Prototyping",
    ],
    image: "/terragrow.webp",
    date: "Oct 2025",
    category: "Academic",
    featured: false,
    github: "https://github.com/Chafic123/agri-space-insight",
    live: "https://agri-space-insight.lovable.app",
  },
  {
    slug: "algopath",
    title: "AlgoPath",
    Icon: SchoolIcon,
    summary:
      "AlgoPath was built in two days for a competition at the BDD building. It is an AI-powered platform that helps students start learning a new programming language by guiding them through the topics they should tackle first.",
    tech: ["AI Integration", "Flask", "React", "MUI", "Version control"],
    image: "/algopath.webp",
    date: "2024",
    category: "Personal",
    featured: false,
  },
  {
    slug: "multi-threading-processing",
    title: "Multi Threading-Processing",
    Icon: SyncAltIcon,
    summary:
      "A full-stack demonstration of the differences between multi-threading and multi-processing, with a React frontend, a Flask backend, and real-time performance metrics collected through Psutil.",
    tech: ["Threads", "React", "Flask", "Psutil", "Version control"],
    image: "/multithreading.webp",
    date: "2024",
    category: "Personal",
    github: "https://github.com/Majdag8/Multi-Processing-Threading",
  },
  {
    slug: "leetcode-75",
    title: "Leetcode Challenge Tracker",
    Icon: CodeIcon,
    summary:
      "Completed the 75 Days of Code series on LeetCode, solving over 80 problems and significantly sharpening my problem-solving skills and algorithmic thinking.",
    tech: [
      "C++",
      "Java",
      "Python",
      "Problem Solving",
      "Algorithms",
      "Data Structures",
    ],
    image: "/leetcode.webp",
    date: "2024",
    category: "Personal",
    github: "https://github.com/Chafic123/Leetcode2024",
  },
  {
    slug: "malaz",
    title: "Malaz",
    Icon: RestaurantIcon,
    summary:
      "A full-stack restaurant application built for an advanced web course, covering online reservations, menu browsing and order management, with a Bootstrap frontend and a PHP/MySQL backend.",
    tech: ["Bootstrap", "MySQL", "PHP", "Version control"],
    image: "/malaz.webp",
    date: "2024",
    category: "Academic",
    // The repo this used to link to 404s; the card renders fine without it.
  },
  {
    slug: "portfolio",
    title: "Portfolio",
    Icon: PersonIcon,
    summary:
      "This site. Built with React 19 and MUI on a design system of semantic tokens rather than per-component styling, so light and dark mode stay in step. Framer Motion handles the reveals, all of it behind prefers-reduced-motion, and the contact form posts through EmailJS.",
    tech: ["React", "Material-UI", "Framer Motion", "EmailJS"],
    image: "/portfolio.webp",
    date: "2026",
    category: "Personal",
    live: "https://www.chafic-achour.com/",
  },
];

/** Projects page header copy. */
export const projectsPage = {
  title: "Projects",
  subtitle:
    "Freelance platforms running in production, competition builds, and the academic work behind them. Every card says what it is and links to it where there is something public to link to.",
};

export const featuredProjects = projects.filter((project) => project.featured);

/** Categories present in the data, in a stable display order. */
export const projectCategories = ["Freelance", "Academic", "Personal"].filter(
  (category) => projects.some((project) => project.category === category)
);
